/* ==========================================================================
   Lavish Custom Cabinetry - ui.js
   Navigation, gallery filter, lightbox, before/after slider, form validation.
   ========================================================================== */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), ' +
                  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, e) {
    var items = Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function lockScroll(on) {
    document.body.classList.toggle('no-scroll', on);
    if (window.lavishLenis) { on ? window.lavishLenis.stop() : window.lavishLenis.start(); }
  }

  /* =====================================================================
     Mobile navigation
     ===================================================================== */
  function initNav(root) {
    var header = root.querySelector('[data-header]');
    if (!header || header.dataset.navDone) return;
    header.dataset.navDone = '1';

    var toggle = header.querySelector('[data-nav-toggle]');
    var panel = header.querySelector('[data-nav-panel]');
    if (!toggle || !panel) return;

    var open = function () {
      header.classList.add('is-open');
      header.classList.remove('is-tucked');
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      // Flush layout so the panel is measurable, then move focus into it.
      void panel.offsetWidth;
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    };

    var close = function (returnFocus) {
      header.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      var hide = function () { if (!header.classList.contains('is-open')) panel.hidden = true; };
      prefersReduced ? hide() : setTimeout(hide, 420);
      if (returnFocus !== false) toggle.focus();
    };

    toggle.addEventListener('click', function () {
      header.classList.contains('is-open') ? close() : open();
    });

    panel.addEventListener('click', function (e) {
      if (e.target.closest('a')) close(false);
      if (e.target === panel) close();
    });

    document.addEventListener('keydown', function (e) {
      if (!header.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key === 'Tab') trapFocus(panel, e);
    });

    // A resize past the desktop breakpoint should not leave the page locked.
    var mq = window.matchMedia('(min-width: 1000px)');
    var onChange = function () { if (mq.matches && header.classList.contains('is-open')) close(false); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
  }

  /* =====================================================================
     Gallery filter - FLIP so tiles glide to their new slot
     ===================================================================== */
  function initFilter(root) {
    root.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.filterDone) return;
      gallery.dataset.filterDone = '1';

      var chips = gallery.querySelectorAll('[data-filter]');
      var grid = gallery.querySelector('[data-gallery-grid]');
      var status = gallery.querySelector('[data-gallery-status]');
      if (!chips.length || !grid) return;

      var apply = function (key) {
        // The tile carries the category, but the element that occupies a slot
        // in the layout may be a wrapper around it. Hide the wrapper, or a
        // masonry column is left holding an empty box.
        var tiles = Array.prototype.slice.call(grid.querySelectorAll('.tile'));
        var items = tiles.map(function (t) { return t.closest('.gal__cell') || t; });

        // FIRST: record where everything currently sits.
        var before = items.map(function (t) { return t.getBoundingClientRect(); });

        var shown = 0;
        tiles.forEach(function (tile, i) {
          var match = key === 'all' || tile.getAttribute('data-category') === key;
          items[i].hidden = !match;
          if (match) shown++;
        });

        if (status) {
          status.textContent = shown + (shown === 1 ? ' project' : ' projects') + ' shown';
        }

        if (prefersReduced) return;

        // LAST + INVERT + PLAY.
        items.forEach(function (tile, i) {
          if (tile.hidden) return;
          var after = tile.getBoundingClientRect();
          var dx = before[i].left - after.left;
          var dy = before[i].top - after.top;
          if (!dx && !dy) {
            tile.animate(
              [{ opacity: 0, transform: 'scale(0.94)' }, { opacity: 1, transform: 'none' }],
              { duration: 480, easing: 'cubic-bezier(0.22,1,0.36,1)' }
            );
            return;
          }
          tile.animate(
            [{ transform: 'translate(' + dx + 'px,' + dy + 'px)' }, { transform: 'none' }],
            { duration: 620, easing: 'cubic-bezier(0.22,1,0.36,1)' }
          );
        });
      };

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          chips.forEach(function (c) {
            var isMe = c === chip;
            c.classList.toggle('is-active', isMe);
            c.setAttribute('aria-pressed', isMe ? 'true' : 'false');
          });
          apply(chip.getAttribute('data-filter'));
        });
      });
    });
  }

  /* =====================================================================
     Lightbox
     ===================================================================== */
  var lightbox = null;

  function buildLightbox() {
    if (lightbox) return lightbox;

    var el = document.createElement('div');
    el.className = 'lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Project image viewer');
    el.hidden = true;
    el.innerHTML =
      '<div class="lightbox__backdrop" data-lb-close></div>' +
      '<figure class="lightbox__figure">' +
        '<img class="lightbox__img" alt="">' +
        '<figcaption class="lightbox__caption t-small"></figcaption>' +
      '</figure>' +
      '<button type="button" class="lightbox__btn lightbox__btn--close" data-lb-close aria-label="Close viewer">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<button type="button" class="lightbox__btn lightbox__btn--prev" data-lb-prev aria-label="Previous image">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>' +
      '</button>' +
      '<button type="button" class="lightbox__btn lightbox__btn--next" data-lb-next aria-label="Next image">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>' +
      '</button>';

    document.body.appendChild(el);

    var state = { items: [], index: 0, trigger: null };
    var img = el.querySelector('.lightbox__img');
    var caption = el.querySelector('.lightbox__caption');
    var figure = el.querySelector('.lightbox__figure');

    var show = function (i) {
      state.index = (i + state.items.length) % state.items.length;
      var item = state.items[state.index];
      img.src = item.src;
      img.alt = item.alt;
      caption.textContent = item.caption;
    };

    var close = function () {
      el.classList.remove('is-open');
      lockScroll(false);
      var hide = function () { el.hidden = true; };
      prefersReduced ? hide() : setTimeout(hide, 320);
      if (state.trigger) state.trigger.focus();
    };

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-lb-close]')) close();
      if (e.target.closest('[data-lb-prev]')) show(state.index - 1);
      if (e.target.closest('[data-lb-next]')) show(state.index + 1);
    });

    document.addEventListener('keydown', function (e) {
      if (el.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); show(state.index - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); show(state.index + 1); }
      else if (e.key === 'Tab') trapFocus(el, e);
    });

    // Swipe on touch.
    var x0 = null;
    figure.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    figure.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 48) show(state.index + (dx < 0 ? 1 : -1));
      x0 = null;
    }, { passive: true });

    lightbox = {
      el: el,
      open: function (items, index, trigger, origin) {
        state.items = items;
        state.trigger = trigger;
        el.hidden = false;
        show(index);
        lockScroll(true);

        if (origin && !prefersReduced) {
          // Grow out of the tile that was clicked.
          var r = origin.getBoundingClientRect();
          figure.style.transformOrigin =
            ((r.left + r.width / 2) / window.innerWidth * 100) + '% ' +
            ((r.top + r.height / 2) / window.innerHeight * 100) + '%';
        }
        // Flush layout so the transition has a start frame, then reveal
        // synchronously. requestAnimationFrame would leave the dialog at
        // opacity 0 while still trapping scroll if frames are starved.
        void el.offsetWidth;
        el.classList.add('is-open');
        el.querySelector('.lightbox__btn--close').focus();
      }
    };
    return lightbox;
  }

  function initLightbox(root) {
    root.querySelectorAll('[data-gallery-grid], [data-lightbox-group]').forEach(function (group) {
      if (group.dataset.lbDone) return;
      group.dataset.lbDone = '1';

      group.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-lightbox-src]');
        if (!btn) return;

        var triggers = Array.prototype.filter.call(
          group.querySelectorAll('[data-lightbox-src]'),
          // Filtered-out tiles must not appear in the lightbox sequence.
          function (t) { return !t.closest('[hidden]'); }
        );
        var items = triggers.map(function (t) {
          var im = t.querySelector('img');
          return {
            src: t.getAttribute('data-lightbox-src'),
            caption: t.getAttribute('data-lightbox-caption') || '',
            alt: im ? im.alt : ''
          };
        });
        buildLightbox().open(items, triggers.indexOf(btn), btn, btn);
      });
    });
  }

  /* =====================================================================
     Before / after slider - mouse, touch, and keyboard
     ===================================================================== */
  function initBeforeAfter(root) {
    root.querySelectorAll('[data-beforeafter]').forEach(function (wrap) {
      if (wrap.dataset.baDone) return;
      wrap.dataset.baDone = '1';

      var handle = wrap.querySelector('[data-ba-handle]');
      var after = wrap.querySelector('[data-ba-after]');
      if (!handle || !after) return;

      var pos = 50;
      var touched = false;

      var paint = function () {
        wrap.style.setProperty('--ba-pos', pos + '%');
        after.style.clipPath = 'inset(0 0 0 ' + pos + '%)';
        handle.setAttribute('aria-valuenow', Math.round(pos));
      };

      var setFromX = function (clientX) {
        var r = wrap.getBoundingClientRect();
        pos = Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100));
        paint();
      };

      var stopIdle = function () {
        if (touched) return;
        touched = true;
        handle.classList.remove('nudge');
      };

      var dragging = false;
      var onDown = function (e) {
        dragging = true;
        stopIdle();
        wrap.classList.add('is-dragging');
        wrap.setPointerCapture && e.pointerId != null && wrap.setPointerCapture(e.pointerId);
        setFromX(e.clientX);
      };
      var onMove = function (e) { if (dragging) setFromX(e.clientX); };
      var onUp = function () { dragging = false; wrap.classList.remove('is-dragging'); };

      wrap.addEventListener('pointerdown', onDown);
      wrap.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);

      handle.addEventListener('keydown', function (e) {
        var step = e.shiftKey ? 10 : 2;
        if (e.key === 'ArrowLeft') { pos = Math.max(0, pos - step); }
        else if (e.key === 'ArrowRight') { pos = Math.min(100, pos + step); }
        else if (e.key === 'Home') { pos = 0; }
        else if (e.key === 'End') { pos = 100; }
        else return;
        e.preventDefault();
        stopIdle();
        paint();
      });

      paint();
    });
  }

  /* =====================================================================
     Contact form - inline validation on blur, non-blocking
     Errors say what is wrong and how to fix it.
     ===================================================================== */
  var MESSAGES = {
    name: 'Tell us your name so we know who we are talking to.',
    email: 'Enter an email address so we can reply, like you@example.com.',
    body: 'Tell us a little about the project so we can give you a useful answer.'
  };

  function initForm(root) {
    root.querySelectorAll('[data-validate]').forEach(function (form) {
      if (form.dataset.formDone) return;
      form.dataset.formDone = '1';

      var fields = form.querySelectorAll('[data-required]');

      var check = function (input, silent) {
        var field = input.closest('.field');
        var slot = field ? field.querySelector('.field__error') : null;
        var value = input.value.trim();
        var key = input.getAttribute('data-required');
        var msg = '';

        if (!value) {
          msg = MESSAGES[key] || 'This one is required.';
        } else if (input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
          msg = MESSAGES.email;
        }

        if (silent && !msg && slot && !slot.textContent) return true;

        if (field) field.classList.toggle('field--error', !!msg);
        if (slot) slot.textContent = msg;
        input.setAttribute('aria-invalid', msg ? 'true' : 'false');
        return !msg;
      };

      fields.forEach(function (input) {
        input.addEventListener('blur', function () { check(input); });
        // Once a field has been flagged, clear it the moment it becomes valid.
        input.addEventListener('input', function () {
          var field = input.closest('.field');
          if (field && field.classList.contains('field--error')) check(input);
        });
      });

      form.addEventListener('submit', function (e) {
        var firstBad = null;
        fields.forEach(function (input) {
          if (!check(input) && !firstBad) firstBad = input;
        });
        if (firstBad) {
          e.preventDefault();
          firstBad.focus();
          firstBad.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'center' });
        }
      });
    });
  }

  /* =====================================================================
     Boot
     ===================================================================== */
  function boot(root) {
    root = root || document;
    initNav(root);
    initFilter(root);
    initLightbox(root);
    initBeforeAfter(root);
    initForm(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  window.lavishUI = { boot: boot };
})();
